import React from 'react';

// ── Colors ───────────────────────────────────────────────────────────────────

export const colors = {
    primary: '#007bff',
    success: '#28a745',
    danger: '#dc3545',
    warning: '#ffc107',
    orange: '#ff8c00',
    info: '#17a2b8',
    secondary: '#6c757d',
    light: '#f8f9fa',
    white: '#fff',
    border: '#ddd',
    textMuted: '#666',
    textDark: '#333',
};

export const getStatusColor = (status: string): string => {
    switch (status) {
        case 'online': return colors.success;
        case 'offline': return colors.secondary;
        case 'error': return colors.danger;
        default: return '#000';
    }
};

export const getDeviceIcon = (type: string): string => {
    switch (type) {
        case 'light': return '💡';
        case 'switch': return '🔌';
        case 'dimmer': return '🎚️';
        case 'thermostat': return '🌡️';
        case 'temperature': return '🌡️';
        case 'humidity': return '💧';
        case 'motion': return '🚶';
        case 'contact': return '🚪';
        case 'router': return '🌐';
        case 'power_meter': return '⚡';
        case 'io_module': return '⚙️';
        default: return '📱';
    }
};

export const getStatusLabel = (status: string): string => {
    switch (status) {
        case 'online': return 'uppkopplad';
        case 'offline': return 'frånkopplad';
        case 'error': return 'fel';
        default: return status;
    }
};

export const getDeviceTypeLabel = (type: string): string => {
    switch (type) {
        case 'light': return 'lampa';
        case 'switch': return 'strömbrytare';
        case 'dimmer': return 'dimmer';
        case 'thermostat': return 'termostat';
        case 'temperature': return 'temperatur';
        case 'humidity': return 'fukt';
        case 'motion': return 'rörelse';
        case 'contact': return 'dörr/fönster';
        case 'router': return 'router';
        case 'power_meter': return 'elmätare';
        case 'io_module': return 'io-modul';
        default: return type;
    }
};

// ── Byggstenar som ui-kitet nedan använder ────────────────────────────────────
// Importera inte dessa direkt; använd `ui`.

const card: React.CSSProperties = { backgroundColor: colors.white, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 20 };
const inputStyle: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: 6,
    padding: '8px 10px', fontSize: 14, fontFamily: 'inherit', background: colors.white,
};
const labelStyle: React.CSSProperties = { display: 'block', fontWeight: 'bold', fontSize: 13, color: '#444', marginBottom: 4 };
const hintStyle: React.CSSProperties = { fontSize: 12, color: '#888', marginTop: 4 };
const btn = (bg: string, color = colors.white): React.CSSProperties => ({
    padding: '8px 16px', backgroundColor: bg, color, border: 'none',
    borderRadius: 6, cursor: 'pointer', fontWeight: 'bold', fontSize: 13, fontFamily: 'inherit',
});
const btnSmall = (bg: string, color = colors.white): React.CSSProperties => ({
    padding: '5px 10px', backgroundColor: bg, color, border: 'none',
    borderRadius: 6, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit',
});
const thStyle: React.CSSProperties = { padding: '10px 12px', textAlign: 'left', fontSize: 13, color: '#555', borderBottom: '2px solid #dee2e6' };
const tdStyle: React.CSSProperties = { padding: '10px 12px', borderBottom: '1px solid #eee', fontSize: 14, verticalAlign: 'top' };

export const msgColor = (msg: string): string =>
    msg.startsWith('Fel') || msg.startsWith('Delvis') ? colors.danger : colors.success;

// ── UI-kit ───────────────────────────────────────────────────────────────────
// Gemensamma byggstenar så att sidorna ser likadana ut. Nya sidor ska använda
// dessa i stället för egna definitioner; gamla sidor byter efter hand.

export const ui = {
    /** Mörkblå som används i flikar och aktiva val. */
    navy: '#0f3460',
    radius: 8,
    /** Knapp i valfri färg (samma form som btnPrimary). */
    btn,
    btnSmall,
    /** Primär knapp (spara, skapa). */
    btnPrimary: {
        padding: '8px 16px', backgroundColor: colors.success, color: colors.white, border: 'none',
        borderRadius: 6, cursor: 'pointer', fontWeight: 'bold', fontSize: 13, fontFamily: 'inherit',
    } as React.CSSProperties,
    /** Sekundär knapp (avbryt, stäng). */
    btnSecondary: {
        padding: '8px 16px', backgroundColor: colors.secondary, color: colors.white, border: 'none',
        borderRadius: 6, cursor: 'pointer', fontWeight: 'bold', fontSize: 13, fontFamily: 'inherit',
    } as React.CSSProperties,
    /** Farlig knapp (ta bort). */
    btnDanger: {
        padding: '8px 16px', backgroundColor: colors.danger, color: colors.white, border: 'none',
        borderRadius: 6, cursor: 'pointer', fontWeight: 'bold', fontSize: 13, fontFamily: 'inherit',
    } as React.CSSProperties,
    /** Liten ljus knapp i tabeller (ändra, visa). */
    btnLight: {
        padding: '5px 10px', backgroundColor: '#e8f0fe', color: '#1a4aa6', border: 'none',
        borderRadius: 6, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit',
    } as React.CSSProperties,
    /** Liten röd knapp i tabeller (ta bort). */
    btnLightDanger: {
        padding: '5px 10px', backgroundColor: '#f8d7da', color: '#721c24', border: 'none',
        borderRadius: 6, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit',
    } as React.CSSProperties,
    /** Liten neutral knapp i tabeller (kopiera, mer). */
    btnLightNeutral: {
        padding: '5px 10px', backgroundColor: '#eee', color: colors.textDark, border: 'none',
        borderRadius: 6, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit',
    } as React.CSSProperties,
    /** Kort med vit bakgrund. */
    card: { ...card, padding: 16, marginBottom: 12 } as React.CSSProperties,
    /** Textfält, rullista och textyta. */
    input: inputStyle,
    label: labelStyle,
    hint: hintStyle,
    /** Tabellhuvud och celler. */
    th: thStyle,
    td: tdStyle,
    /** Fel- och lyckat-meddelanden. */
    errorBox: {
        backgroundColor: '#f8d7da', color: '#721c24', padding: 12, borderRadius: 6, marginBottom: 16,
        display: 'flex', justifyContent: 'space-between', gap: 10,
    } as React.CSSProperties,
    successBox: {
        backgroundColor: '#d4edda', color: '#155724', padding: 12, borderRadius: 6, marginBottom: 16,
        display: 'flex', justifyContent: 'space-between', gap: 10,
    } as React.CSSProperties,
    /** Flikknapp; skicka in om den är vald. */
    tab: (active: boolean): React.CSSProperties => ({
        padding: '10px 18px', border: 'none', borderRadius: '6px 6px 0 0', cursor: 'pointer', fontSize: 14, whiteSpace: 'nowrap',
        backgroundColor: active ? '#0f3460' : 'transparent', color: active ? colors.white : '#555', fontWeight: active ? 'bold' : 'normal',
    }),
    /** Litet statusmärke, t.ex. "Gjord" eller "Försenad". */
    badge: (bg: string, color = colors.white): React.CSSProperties => ({
        background: bg, color, borderRadius: 10, padding: '2px 9px', fontSize: 12, whiteSpace: 'nowrap',
    }),
};
